// ABOUTME: Column editor for fixing Databricks column name/type errors during CSV ingestion.
// ABOUTME: Shows a table of columns with editable names and type dropdowns for retry-with-schema.

import { useState, useCallback } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';

const SUPPORTED_TYPES = [
  'STRING',
  'INT',
  'BIGINT',
  'DOUBLE',
  'FLOAT',
  'BOOLEAN',
  'DATE',
  'TIMESTAMP',
  'TIMESTAMP_NTZ',
  'DECIMAL(18,2)',
] as const;

export interface InferredColumn {
  name: string;
  type: string;
  hasError: boolean;
}

export interface ColumnMapping {
  originalName: string;
  originalType: string;
  newName: string;
  newType: string;
}

interface ColumnEditorProps {
  columns: InferredColumn[];
  onRetry: (mappings: ColumnMapping[]) => void;
  onCancel: () => void;
  isRetrying: boolean;
}

function sanitizeColumnName(name: string): string {
  let safe = name.toLowerCase().replace(/[\s\-\.]+/g, '_').replace(/[^a-z0-9_]/g, '');
  safe = safe.replace(/^[0-9]+/, '');
  if (!safe) safe = 'column';
  if (!safe[0].match(/[a-z_]/)) safe = `c_${safe}`;
  return safe.slice(0, 128);
}

export function ColumnEditor({ columns, onRetry, onCancel, isRetrying }: ColumnEditorProps) {
  const [mappings, setMappings] = useState<ColumnMapping[]>(() =>
    columns.map((col) => ({
      originalName: col.name,
      originalType: col.type,
      newName: sanitizeColumnName(col.name),
      newType: SUPPORTED_TYPES.includes(col.type.toUpperCase() as typeof SUPPORTED_TYPES[number])
        ? col.type.toUpperCase()
        : 'STRING',
    }))
  );

  const updateName = useCallback((index: number, newName: string) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, newName } : m))
    );
  }, []);

  const updateType = useCallback((index: number, newType: string) => {
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, newType } : m))
    );
  }, []);

  const handleRetry = () => {
    onRetry(mappings);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-surface-600">
        <AlertCircle className="w-4 h-4 text-amber-500" />
        <span>Review and fix column names and types below, then retry ingestion.</span>
      </div>

      <div className="overflow-x-auto border border-surface-200 rounded-lg max-h-[60vh]">
        <table className="w-full text-sm" role="table">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-surface-200 bg-surface-100">
              <th className="px-4 py-2.5 text-left text-surface-600 font-medium">Original Name</th>
              <th className="px-4 py-2.5 text-left text-surface-600 font-medium">New Name</th>
              <th className="px-4 py-2.5 text-left text-surface-600 font-medium w-48">Type</th>
            </tr>
          </thead>
          <tbody>
            {columns.map((col, idx) => (
              <tr
                key={col.name}
                className={`border-b border-surface-100 ${col.hasError ? 'bg-red-500/10' : ''}`}
                data-testid={col.hasError ? 'error-column-row' : 'column-row'}
              >
                <td className="px-4 py-2 font-mono text-xs text-surface-900 whitespace-nowrap">
                  {col.name}
                  {col.hasError && (
                    <AlertCircle className="w-3 h-3 inline-block ml-1.5 text-red-500" />
                  )}
                  <span className="block text-surface-400 text-[10px] mt-0.5">{col.type}</span>
                </td>
                <td className="px-4 py-2">
                  <input
                    type="text"
                    value={mappings[idx]?.newName ?? ''}
                    onChange={(e) => updateName(idx, e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm text-surface-900 bg-surface-50 border border-surface-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    aria-label={`New name for ${col.name}`}
                  />
                </td>
                <td className="px-4 py-2">
                  <select
                    value={mappings[idx]?.newType ?? 'STRING'}
                    onChange={(e) => updateType(idx, e.target.value)}
                    className="w-full px-2.5 py-1.5 text-sm text-surface-900 bg-surface-50 border border-surface-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    aria-label={`Type for ${col.name}`}
                  >
                    {SUPPORTED_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} disabled={isRetrying}>
          Cancel
        </Button>
        <Button onClick={handleRetry} disabled={isRetrying}>
          {isRetrying ? (
            <>
              <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              Retrying...
            </>
          ) : (
            'Retry with fixes'
          )}
        </Button>
      </div>
    </div>
  );
}
